import React, { JSX, useRef } from "react";
import { AgGridReact } from "ag-grid-react";
import "./styles.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faHome, faUser, faFloppyDisk, faXmark } from "@fortawesome/free-solid-svg-icons";
import FloatingActions, { FloatingAction } from "../FloatingActions";
import Card from "../Card";
import UserSelectAdd, { UserOption } from "../UserSelectAdd";
import axios from "axios";
import { User } from "../UserSettings/types";
import { AG_GRID_LOCALE_CZ } from '@ag-grid-community/locale';
import { GridApi, themeQuartz } from "ag-grid-community";
import { UiColumn, UiColumn2 } from "../ItemList/indexOld";
import { EditListItem } from "../EditListItem";

const myTheme = themeQuartz.withParams({
    fontSize: 17,
    headerFontSize: 17,
    rowHeight: 55,
    headerHeight: 48,
});

export interface appListsSettingsProps {
    listId?: string;
    onSave?: (listId: string) => void;
}

export interface ListProps {
    id: string;
    name?: string;
    description?: string;
}

export interface ListItemProps {
    id: string;
    name?: string;
    active?: boolean;
    edited?: boolean;
}

const ListsSettings: React.FC<appListsSettingsProps> = ({ listId, onSave }) => {

    const listItemsColumnsStocks: UiColumn2<ListItemProps>[] = [
        { field: "name", headerName: "Položka číselníku", flex: 3, filter: 'agTextColumnFilter' },
        { field: "active", headerName: "Aktivní", flex: 1, maxWidth: 150, filter: 'agSetColumnFilter' }
    ]

    const [name, setName] = React.useState<string | null>(null);
    const [description, setDescription] = React.useState<string | null>(null);
    const [listItems, setListItems] = React.useState<ListItemProps[]>([]);
    const [selectedListItem, setSelectedListItem] = React.useState<string | null>(null);
    const [displayDialogListItem, setDisplayDialogListItem] = React.useState<boolean>(false);

    const [listItemName, setListItemName] = React.useState<string | null>(null);
    const [listItemActive, setListItemActive] = React.useState<boolean>(false);


    const gridApiRef = useRef<GridApi | null>(null);

    const [error, setError] = React.useState<string | null>(null);

    React.useEffect(() => {
        setError(null);

        loadData();
    }, []);

    const loadData = async () => {
        if (listId) {
            await loadList(listId);
            await loadListItems(listId);
        }
    }

    const setListData = (list: ListProps) => {
        setName(list.name);
        setDescription(list.description);
    }

    const loadList = async (listId: string) => {
        try {
            const res = await (
                axios({
                    method: "post",
                    url: "api/listitems/get",
                    data: {
                        listId: listId
                    }
                })
            );

            if (res.data.list.length > 0) {
                const list: ListProps = res.data.list[0];
                setListData(list);
            }
        } catch (err) {
            throw err;
        }
    }

    const loadListItems = async (listId: string) => {
        try {
            const res = await (
                axios({
                    method: "post",
                    url: "api/listitems/getAllListItems",
                    data: {
                        listId: listId
                    }
                })
            );

            if (res.data.listItems.length > 0) {
                setListItems(res.data.listItems);
            } else {
                setListItems([]);
            }
        } catch (err) {
            throw err;
        }
    }

    const insertList = async (name: string, description: string) => {
        try {
            const res = await (
                axios({
                    method: "post",
                    url: "api/listitems/createList",
                    data: {
                        name: name,
                        description: description
                    }
                })
            );

            return res.data.id;
        } catch (err) {
            throw err;
        }
    }

    const updateList = async (listId: string, name: string, description: string) => {
        try {
            const res = await (
                axios({
                    method: "post",
                    url: "api/listitems/updateList",
                    data: {
                        id: listId,
                        name: name,
                        description: description
                    }
                })
            );
        } catch (err) {
            throw err;
        }
    }

    const updateListItem = async (listItemId: string, name: string, active: boolean) => {
        try {
            const res = await (
                axios({
                    method: "post",
                    url: "api/listitems/updateListItem",
                    data: {
                        id: listItemId,
                        name: name,
                        active: active
                    }
                })
            );
        } catch (err) {
            throw err;
        }
    }

    const createListItem = async (listId: string, name: string, active: boolean) => {
        try {
            const res = await (
                axios({
                    method: "post",
                    url: "api/listitems/createListItem",
                    data: {
                        name: name,
                        listId: listId,
                        active: active
                    }
                })
            );
        } catch (err) {
            throw err;
        }
    }

    const validateList = () => {
        const fn = name?.trim();

        if (!fn) {
            return "Vyplňte název.";
        }

        return null;
    };

    const onCreateListItems = async (listId: string) => {
        const listItemsNew = listItems.filter(listItem => isNaN(Number(listItem?.id)));
        const listUtemUpdated = listItems.filter(listItem => listItem?.edited);

        for (const listItemNew of listItemsNew) {
            await createListItem(listId, listItemNew?.name, listItemNew?.active);
        }

        for (const listItemUpdated of listUtemUpdated) {
            await updateListItem(listItemUpdated.id, listItemUpdated?.name, listItemUpdated?.active);
        }
    };

    const onCreate = async () => {
        setError(null);

        const validationError = validateList();
        if (validationError) {
            setError(validationError);
            return;
        }

        if (listId) {
            await updateList(listId, name, description);
            await onCreateListItems(listId);
            onSave(listId);
        } else {
            const newListId = await insertList(name, description);
            await onCreateListItems(newListId);
            onSave(newListId);
        }
    };

    const onClickNewListItem = async () => {
        setListItemName("");
        setListItemActive(true);
        setSelectedListItem(null);
        setDisplayDialogListItem(true);
    };

    const onClickUpdateListItem = async () => {
        const ids = gridApiRef.current.getSelectedNodes().map(n => n.data.id);

        if (ids.length > 0) {
            const selectedListItem = listItems.filter(listItem => listItem.id === ids[0]);

            if (selectedListItem && selectedListItem.length > 0) {
                setSelectedListItem(selectedListItem[0]?.id);
                setListItemName(selectedListItem[0]?.name);
                setListItemActive(selectedListItem[0]?.active);

                setDisplayDialogListItem(true);
            }
        }
    };

    const onClickSaveListItem = async () => {

        if (listItemName) {
            if (selectedListItem) {
                setListItems(items =>
                    items.map(item =>
                        item.id === selectedListItem
                            ? { ...item, name: listItemName, active: listItemActive, edited: true }
                            : item
                    )
                );
            } else {
                setListItems(items => [
                    ...items,
                    {
                        id: crypto.randomUUID(),
                        name: listItemName,
                        active: listItemActive
                    }
                ]);
            }

            setListItemName(null);
            setListItemActive(false);
            setDisplayDialogListItem(false);
        }
    };

    const onClickBackListItem = async () => {
        setListItemName(null);
        setListItemActive(false);
        setDisplayDialogListItem(false);
    };

    const onGridReady = (params) => {
        gridApiRef.current = params.api;
    };

    const actions: FloatingAction[] = [
        {
            key: "cancel",
            label: "Zrušit",
            icon: <FontAwesomeIcon icon={faXmark} />,
            onClick: () => onSave(null),
        },
        {
            key: "save",
            label: "Uložit",
            icon: <FontAwesomeIcon icon={faFloppyDisk} />,
            onClick: onCreate,
        },
    ];

    return <>{
        displayDialogListItem && (
            <EditListItem
                listItemName={listItemName}
                setListItemName={setListItemName}
                listItemActive={listItemActive}
                setListItemActive={setListItemActive}
                error={error}
                onBack={onClickBackListItem}
                onSave={onClickSaveListItem}
            />
        )}
        <Card title="Číselník" suppressInit={true} displaySelectedStock={false}>
            <div className="nec-stack">
                <>
                    <div className="nec-block">
                        <div className="nec-header">Název</div>
                        <div className="nec-control">
                            <input
                                className="nec-input"
                                value={name}
                                required
                                onChange={(e) => setName(e.target.value)}
                                placeholder="Název..."
                            />
                        </div>
                    </div>


                    <div className="nec-block">
                        <div className="nec-header">Popis</div>
                        <div className="nec-control">
                            <input
                                className="nec-input"
                                value={description}
                                required
                                onChange={(e) => setDescription(e.target.value)}
                                placeholder="Popis..."
                            />
                        </div>
                    </div>

                    <div className="item-list-toolbar-right1">
                        <button
                            type="button"
                            className={`item-list-tab is-active`}
                            onClick={() => onClickNewListItem()}
                        >
                            Nový
                        </button>
                        <button
                            type="button"
                            className={`item-list-tab is-active`}
                            onClick={() => onClickUpdateListItem()}
                        >
                            Upravit
                        </button>
                    </div>
                    <div className="item-list-grid-wrapper1">
                        <AgGridReact
                            theme={myTheme}
                            localeText={AG_GRID_LOCALE_CZ}
                            columnDefs={listItemsColumnsStocks}
                            rowData={listItems}
                            pagination={false}
                            rowSelection="single"
                            onGridReady={onGridReady}
                        />
                    </div>
                </>

                {error ? <div className="form-error">{error}</div> : null}
            </div>

            {actions?.length ? (
                <div className="item-list-actions">
                    <FloatingActions actions={actions} />
                </div>
            ) : null}
        </Card>
    </>

};

export default ListsSettings;